/**
 * Deployment construct with sane security defaults
 *
 * Provides 80/20 utility for creating Kubernetes Deployments with:
 * - 1 replica (default)
 * - RollingUpdate strategy
 * - Non-root security context
 * - Development profile (writable filesystem for live updates) or production profile (read-only filesystem)
 * - Standard app labels
 */

import type {
  Deployment,
  DeploymentSpec,
  Container,
  PodSpec,
  PodTemplateSpec,
  ResourceRequirements,
  ContainerPort,
  PodSecurityContext,
  Volume,
  VolumeMount,
} from "@tilt-ts/k8s-schemas/dist/src/generated/k8s/v1.31/apps/manifest";

import { validateDeployment } from "@tilt-ts/k8s-schemas/dist/src/generated/k8s/v1.31/apps/manifest";

import {
  createManifestWrapper,
  createWrapperFromFile,
  createWrapperFromString,
  type K8sManifestWrapper,
} from "./k8s-manifest-wrapper";

/**
 * Production pod security context - strict settings for production workloads
 * - Runs as non-root user/group
 * - RuntimeDefault seccomp profile
 */
export const PRODUCTION_POD_SECURITY_CONTEXT: PodSecurityContext = {
  runAsNonRoot: true,
  runAsUser: 1000,
  runAsGroup: 3000,
  fsGroup: 2000,
  seccompProfile: {
    type: "RuntimeDefault",
  },
};

/**
 * Development pod security context - relaxed settings for live development
 * - Runs as non-root user/group
 * - RuntimeDefault seccomp profile
 * - No specific user/group constraints for maximum flexibility
 */
export const DEVELOPMENT_POD_SECURITY_CONTEXT: PodSecurityContext = {
  runAsNonRoot: false, // Allow running as root in development for maximum flexibility
  seccompProfile: {
    type: "RuntimeDefault",
  },
};

/** Container security context for production workloads
 * - Non-root user
 * - Read-only root filesystem
 * - Drops all capabilities
 */
export const PROD_CONTAINER_SECURITY_CONTEXT = {
  allowPrivilegeEscalation: false,
  readOnlyRootFilesystem: false,
  runAsNonRoot: true,
  capabilities: {
    drop: ["ALL"],
  },
};

/**
 * Container security context for live development
 * - Writable root filesystem (allows live updates and file syncing)
 * - Relaxed permissions for development workflow
 */
export const DEV_CONTAINER_SECURITY_CONTEXT = {
  allowPrivilegeEscalation: false,
  readOnlyRootFilesystem: false,
};

export interface DeploymentOptions {
  /** Kubernetes namespace (defaults to 'default') */
  namespace?: string;
  /** Number of replicas (defaults to 1) */
  replicas?: number;
  /** Additional labels to merge with standard app labels */
  labels?: Record<string, string>;
  /** Container ports to expose */
  ports?: number[];
  /** Environment variables (supports full K8s EnvVar spec including valueFrom) */
  env?: Container["env"];
  /** Environment variables from ConfigMaps or Secrets */
  envFrom?: Container["envFrom"];
  /** Resource requirements (requests/limits) */
  resources?: ResourceRequirements;
  /**
   * Pod-level security context (defaults to DEVELOPMENT_POD_SECURITY_CONTEXT)
   * Use PRODUCTION_POD_SECURITY_CONTEXT for production deployments
   */
  securityContext?: PodSecurityContext;
  /** Volumes to attach to the pod */
  volumes?: Volume[];
  /** Volume mounts for the container */
  volumeMounts?: VolumeMount[];
}

/**
 * Creates a Kubernetes Deployment with sane security defaults
 *
 * @param app - Application name (used for labels and metadata)
 * @param image - Container image (e.g., 'nginx:1.21')
 * @param opts - Optional configuration
 * @returns Complete Deployment manifest
 *
 * @example
 * ```typescript
 * // Development deployment with writable filesystem (default - for live updates)
 * const devDeploy = deployment('my-app', 'nginx:1.21', {
 *   replicas: 1,
 *   ports: [8080]
 * });
 *
 * // Production deployment with strict security
 * const prodDeploy = deployment('my-app', 'nginx:1.21', {
 *   securityContext: PRODUCTION_POD_SECURITY_CONTEXT,
 *   replicas: 3,
 *   ports: [8080],
 *   resources: {
 *     requests: { cpu: '100m', memory: '128Mi' },
 *     limits: { cpu: '500m', memory: '512Mi' }
 *   }
 * });
 *
 * // Note: Container always uses CONTAINER_SECURITY_CONTEXT (writable filesystem)
 * // for live update support. Use read-only volumes for production data protection.
 * ```
 */
export function deployment(
  app: string,
  image: string,
  opts: DeploymentOptions = {}
): K8sManifestWrapper<Deployment> {
  const {
    namespace = "default",
    replicas = 1,
    labels = {},
    ports = [],
    env = [],
    envFrom,
    resources,
    securityContext,
    volumes,
    volumeMounts,
  } = opts;

  // Standard app labels
  const standardLabels = {
    app,
    "app.kubernetes.io/name": app,
    "app.kubernetes.io/component": "application",
    "app.kubernetes.io/part-of": app,
    ...labels,
  };

  // Convert port numbers to ContainerPort objects
  const containerPorts: ContainerPort[] = ports.map((port) => ({
    containerPort: port,
    protocol: "TCP",
  }));

  // Select security contexts - default to development profile
  const podSecurityContext =
    securityContext || DEVELOPMENT_POD_SECURITY_CONTEXT;

  // Container definition with security defaults
  const container: Container = {
    name: app,
    image,
    ports: containerPorts.length > 0 ? containerPorts : undefined,
    env: env.length > 0 ? env : undefined,
    ...(envFrom && envFrom.length > 0 && { envFrom }),
    ...(resources && { resources }),
    ...(volumeMounts && volumeMounts.length > 0 && { volumeMounts }),
    securityContext: DEV_CONTAINER_SECURITY_CONTEXT,
  } as Container;

  // Pod specification
  const podSpec: PodSpec = {
    containers: [container],
    restartPolicy: "Always",
    securityContext: podSecurityContext,
    ...(volumes && volumes.length > 0 && { volumes }),
  };

  // Pod template
  const podTemplate: PodTemplateSpec = {
    metadata: {
      labels: standardLabels,
    },
    spec: podSpec,
  };

  // Deployment specification with RollingUpdate strategy
  const spec: DeploymentSpec = {
    replicas,
    selector: {
      matchLabels: {
        app,
      },
    },
    template: podTemplate,
    strategy: {
      type: "RollingUpdate",
      rollingUpdate: {
        maxSurge: 1,
        maxUnavailable: 1,
      },
    },
  };

  // Complete Deployment manifest
  const deploymentData: Deployment = {
    apiVersion: "apps/v1",
    kind: "Deployment",
    metadata: {
      name: app,
      namespace,
      labels: standardLabels,
    },
    spec,
  };

  return createManifestWrapper(deploymentData, validateDeployment);
}

// Static factory methods for loading deployments from files/strings
deployment.fromFile = (path: string) => {
  return createWrapperFromFile<Deployment>(path, validateDeployment);
};

deployment.fromString = (content: string) => {
  return createWrapperFromString<Deployment>(content, validateDeployment);
};
